import { useContext } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { OrganisationTypeConstants } from "../../../../constants";
import { Origin, TabsList } from "../../../../models";
import { IStateOrigin } from "../../../../navigators/navigateStateObjects";
import { GetProjectDetailsResponse as GetPublicProjectDetailsResponse } from "../../../../service/publicquery";
import { GetProjectDetailsResponse } from "../../../../service/query";
import { useAuth } from "../../../../useAuth";
import {
  getDashboardRoute,
  getProjectAuditHistoryRoute,
  getProjectDetailsByUuid,
  getProjectDetailsRoute,
  getProjectDocumentsRoute,
  getProjectInventoryRoute,
  getProjectLocationRoute,
  getProjectsRoute,
  getProjectTasksRoute,
  getSettingsAuditHistoryRoute,
  getWorkflowTemplatesRoute,
} from "../../../../utils/routes";
import { ProjectContext } from "../../../shared/projects/project/ProjectContext";

interface UseProjectReturnData {
  projectDetails: GetProjectDetailsResponse | undefined;
  publicProjectDetails: GetPublicProjectDetailsResponse | undefined;
  handleBackClick: () => void;
  tabsList: TabsList;
  backButtonText: string;
}

export const useProject = (): UseProjectReturnData => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const { state } = useLocation();
  const stateOrigin = state?.origin as IStateOrigin;
  const { currentUserType } = useAuth();
  const { projectDetails, publicProjectDetails, isPreAccess } = useContext(ProjectContext);

  const origin = searchParams.get("origin");

  let backButtonText = stateOrigin?.goBackText;

  if (!backButtonText) {
    switch (origin) {
      case Origin.ProjectAuditPage:
      case Origin.OrganisationAuditPage:
        backButtonText = "Back to audit";
        break;
      case Origin.WorkflowsPage:
        backButtonText = "Back to workflows";
        break;
      case Origin.DashboardPage:
        backButtonText = "Back to dashboard";
        break;
      default:
        backButtonText = "Back to projects";
    }
  }

  const handleBackClick = (): void => {
    if (stateOrigin?.from) {
      navigate(stateOrigin.from);
      return;
    }

    switch (origin) {
      case Origin.ProjectAuditPage:
        if (projectDetails?.uuid) {
          navigate(getProjectAuditHistoryRoute(projectDetails.uuid, currentUserType));
        }
        break;
      case Origin.OrganisationAuditPage:
        navigate(getSettingsAuditHistoryRoute(currentUserType));
        return;
      case Origin.WorkflowsPage:
        navigate(getWorkflowTemplatesRoute(currentUserType));
        return;
      case Origin.DashboardPage:
        navigate(getDashboardRoute(currentUserType));
        return;
      default:
        navigate(getProjectsRoute(currentUserType));
    }
  };

  const getTabsList = (): TabsList => {
    const list = [
      {
        label: "Overview",
        route: `/d/projects/${projectDetails?.uuid}/overview${origin ? `?origin=${origin}` : ""}`,
      },
      {
        label: "Details",
        route: getProjectDetailsRoute(
          projectDetails?.uuid || "",
          OrganisationTypeConstants.DEVELOPER,
          origin
            ? {
                origin,
              }
            : undefined
        ),
      },
      {
        label: "Location",
        route: getProjectLocationRoute(
          projectDetails?.uuid || "",
          OrganisationTypeConstants.DEVELOPER,
          origin
            ? {
                origin,
              }
            : undefined
        ),
      },
      {
        label: "Activities",
        route: `/d/projects/${projectDetails?.uuid}/activities${origin ? `?origin=${origin}` : ""}`,
      },
      {
        label: "Marketing",
        route: `/d/projects/${projectDetails?.uuid}/marketing-settings${origin ? `?origin=${origin}` : ""}`,
      },
      {
        label: "Documents",
        route: `/d/projects/${projectDetails?.uuid}/documents${origin ? `?origin=${origin}` : ""}`,
      },
    ];

    // REQ-014: Inventory tab is hidden when the project has no unit measurements (null or empty, e.g. No standard projects).
    if (projectDetails?.unitMeasurements?.length) {
      const locationIndex = list.findIndex((tab) => tab.label === "Location");
      list.splice(locationIndex + 1, 0, {
        label: "Inventory",
        route: `/d/projects/${projectDetails?.uuid}/inventory${origin ? `?origin=${origin}` : ""}`,
      });
    }

    list.push({
      label: "Access",
      route: `/d/projects/${projectDetails?.uuid}/access${origin ? `?origin=${origin}` : ""}`,
    });

    list.push({
      label: "Tasks",
      route: getProjectTasksRoute(projectDetails?.uuid || "", OrganisationTypeConstants.DEVELOPER),
    });

    return list;
  };

  // The pre-access view only has the public details, so it offers just the tabs that can render from them.
  const getPreAccessTabsList = (): TabsList => {
    // The public details failed to load (ProjectContext has said so), so there is nothing a tab could show.
    if (!publicProjectDetails) return [];

    const { uuid } = publicProjectDetails;
    const routeParams = origin ? { origin } : undefined;

    return [
      { label: "Overview", route: getProjectDetailsByUuid(uuid, OrganisationTypeConstants.DEVELOPER, routeParams) },
      // REQ-014: Inventory tab is hidden when the project has no unit measurements (null or empty, e.g. No standard projects).
      ...(publicProjectDetails?.unitMeasurements?.length
        ? [
            {
              label: "Inventory",
              route: getProjectInventoryRoute(uuid, OrganisationTypeConstants.DEVELOPER, routeParams),
            },
          ]
        : []),
      { label: "Documents", route: getProjectDocumentsRoute(uuid, OrganisationTypeConstants.DEVELOPER, routeParams) },
    ];
  };

  return {
    projectDetails,
    publicProjectDetails,
    handleBackClick,
    tabsList: isPreAccess ? getPreAccessTabsList() : getTabsList(),
    backButtonText,
  };
};
